<template>
    <div
        :class="{
            'reverse': item.is_from_client,
            'hidden': !item.visible_to_client,
            'status-changed': item.type === 'status-changed',
        }"
        class="message-grid"
    >
        <template
            v-if="item.type === 'status-changed'"
        >
            <div class="status-icon">
                <i
                    v-b-tooltip
                    class="fa fa-bolt"
                    :title="$t('message.status-changed')"
                />
            </div>
            <div class="status-message">
                <div>
                    {{ $t('message.status-changed-to') }} <strong class="mr-1">{{ item.data.status.name }}</strong>
                    <template v-if="item.actor">
                        {{ $t('actions.by') }}
                        <Operator
                            v-if="item.actor.is_operator > 0"
                            :model-value="item.actor"
                        />
                        <Client
                            v-else
                            :model-value="item.actor"
                        />
                    </template>
                </div>
                <small class="text-muted ml-2">
                    <DateFromNow :model-value="item.created_at"/>
                </small>
            </div>
        </template>
        <template v-else>
            <div
                class="status-external cloud-info"
            >
                <div
                    class="status-message"
                >
                    <div>
                        <i
                            v-b-tooltip
                            class="fa mr-1"
                            :class="{ [`fa-${iconMessage()[1]}`]: true }"
                            :title="iconMessage()[0]"
                        />

                        {{ textMessage() }}
                    </div>
                    <div>
                        <b-form-row>
                            <b-col
                                class="align-self-center text-nowrap"
                            >
                                <small class="text-muted">
                                    <DateFromNow :model-value="$dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')"/>
                                </small>
                            </b-col>
                        </b-form-row>
                    </div>
                </div>
            </div>
            <div class="message-icon">
                <Gravatar
                    :id="`from-gravatar-${item.id}`"
                    :user="item.actor"
                    :email="item.original_email_from"
                    :external="isFromExternal"
                    :link="false"
                />
                <b-tooltip
                    triggers="hover"
                    :target="`from-gravatar-${item.id}`"
                    placement="top"
                    custom-class="message-tooltip"
                >
                    <span v-if="item.actor">
                        {{ $t('message.from') }}: {{ item.actor.full_name }}
                    </span>
                </b-tooltip>
            </div>
            <div
                class="message-cloud"
            >
                <div
                    class="message-cloud-background"
                    :class="{ wiggle: checkForHash(item.id), editing: isEditing() }"
                >
                    <div
                        v-if="!isEditing()"
                    >
                        <CustomIframe
                            :id="item.id"
                            :model-value="item.message"
                        />
                    </div>
                    <template v-else>
                        <b-form-group>
                            <Editor
                                v-model="item.message"
                                :message-id="item.id"
                            />
                        </b-form-group>
                        <b-form-row class="ml-3 mb-3">
                            <b-col>
                                <button
                                    class="btn btn-primary btn-sm"
                                    @click="update()"
                                >
                                    {{ $t('actions.store') }}
                                </button>
                            </b-col>
                            <b-col v-if="object || assetDetailPage">
                                <b-form-checkbox v-model="item.visible_to_client">
                                    {{
                                        $t('message.visible-to-client')
                                    }}
                                </b-form-checkbox>
                            </b-col>
                        </b-form-row>
                    </template>
                    <div v-if="item.attachments && item.attachments.length">
                        <hr
                            class="my-1"
                            style="border-color: #ddd"
                        />
                        <p class="text-right m-2">
                            <template
                                v-for="attachment in item.attachments"
                                v-bind:key="attachment.id"
                            >
                                <a
                                    class="btn btn-sm btn-outline-dark ml-1"
                                    :href="`${attachment.url}?name=${attachment.name}&token=${attachment.token}`"
                                    target="_blank"
                                >
                                    <i :class="extensionToIcon(attachment.extension)"/> {{ attachment.name }}
                                    ({{ formatSize(attachment.size) }})
                                </a>
                            </template>
                        </p>
                    </div>
                </div>
                <div class="cloud-recipients">
                    <div :id="`to-gravatar-${item.id}`">
                        <Gravatar
                            v-for="(recipient, index) in item.recipients"
                            :key="index"
                            :email="recipient.address"
                            :external="!recipient.isClient"
                            :user="recipient.user"
                            :gravatar="recipient.gravatar"
                            :small="true"
                            :link="true"
                        />
                    </div>
                    <b-tooltip
                        triggers="hover"
                        :target="`to-gravatar-${item.id}`"
                        placement="top"
                        custom-class="message-tooltip"
                    >
                        <span>
                            <template v-if="item.to">{{ $t('message.to') }}: {{ item.to }}</template>
                            <template v-if="item.to && (item.cc)"><br></template>
                            <template v-if="item.cc">{{ $t('message.cc') }}: {{ item.cc }}</template>
                            <template v-if="(item.to || item.cc) && (item.bcc)"><br></template>
                            <template v-if="item.bcc">{{ $t('message.bcc') }}: {{ item.bcc }}</template>
                        </span>
                    </b-tooltip>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import Message from '@/models/Message';
import eventBus from '@/services/eventBus';
import CustomIframe from '@/shared/CustomIframe.vue';
import Editor from '@/shared/Editor.vue';
import ExtensionToIcon from '@/shared/extension-to-icon';
import MixinEditing from '@/shared/mixin-editing';
import Client from '@/shared/model/Client.vue';
import DateFromNow from '@/shared/model/DateFromNow.vue';
import Gravatar from '@/shared/model/Gravatar.vue';
import Operator from '@/shared/model/Operator.vue';

export default {
    components: {
        Editor,
        CustomIframe,
        Gravatar,
        DateFromNow,
        Client,
        Operator
    },
    mixins: [MixinEditing],
    props: {
        item: Message,
        object: {
            type: Object,
            default: () => {
            },
        },
    },
    computed: {
        assetDetailPage () {
            return this.$route.name.includes('assets');
        },
        time () {
            return this.$dayjs(this.item.created_at).format('HH:mm:ss');
        },
        date () {
            return this.$dayjs(this.item.created_at).format('YYYY-MM-DD');
        },
        isFromExternal () {
            if (!this.object) {
                return;
            }

            return (!this.object.client_id || this.item.actor_id !== this.object.client_id)
                && this.item.is_from_client
                && this.item.original_email_from !== (this.object.client ? this.object.client.email : this.object.original_email_from);
        },
    },
    methods: {
        checkForHash (id) {
            return this.$route.hash === `#message-${id}`;
        },
        iconMessage () {
            if (!this.item.visible_to_client) {
                return ['', 'eye-slash'];
            } else if (this.item.type === 'work-order-signed') {
                return [this.$t('message.work-order-signed'), 'file-contract'];
            } else if (!this.item.is_from_client && this.item.visible_to_client && !this.item.send_to_client) {
                return ['', 'sticky-note'];
            } else {
                return ['', 'comment'];
            }
        },
        textMessage () {
            let message = '';
            if (this.item.is_from_client) {
                message = 'message.sent-message';
            } else if (this.item.send_to_client) {
                message = 'message.received-message';
            } else {
                message = 'message.note';
            }

            return this.$t(message);
        },
        update () {
            const dirtyModel = this.item.getDirty();
            if (Object.keys(dirtyModel).length === 0) {
                this.unEdit();
                return;
            }

            return this.item.update(dirtyModel).then(() => {
                this.unEdit();
                eventBus.emit('refresh');
            });
        },
        extensionToIcon (ext) {
            return ExtensionToIcon(ext);
        },
        destroy () {
            return this.item.destroy().then(() => {
                eventBus.emit('reload-data');
            });
        },
    },
};
</script>

<style lang="scss">
.message-grid .action-buttons {
    visibility: hidden;
}

.message-grid:hover .action-buttons {
    visibility: initial;
}

.message-tooltip .tooltip-inner {
    text-align: left;
    max-width: 400px;
}

@keyframes wiggle {
    0% {
        transform: translate(1px, 0px);
    }
    10% {
        transform: translate(-1px, 0px);
    }
    20% {
        transform: translate(-3px, 0px);
    }
    30% {
        transform: translate(3px, 0px);
    }
    40% {
        transform: translate(1px, 0px);
    }
    50% {
        transform: translate(-1px, 0px);
    }
    60% {
        transform: translate(-3px, 0px);
    }
    70% {
        transform: translate(3px, 0px);
    }
    80% {
        transform: translate(-1px, 0px);
    }
    90% {
        transform: translate(1px, 0px);
    }
    100% {
        transform: translate(1px, 0px);
    }
}

.wiggle {
    animation: wiggle 0.5s;
    animation-delay: 1.2s;
}
</style>
